/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericCheckbox from "@ui-components/Checkbox/GenericCheckbox";
import GenericCard from "@ui-components/Card/GenericCard";
import { SkeletonText, SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { DataCenter } from "./DcData";

// ====================== Endpoint rendering ======================
// A DCI's A side is always a port. The B side is polymorphic: a port
// (legacy port-to-port) or a non-port service (SD-WAN hub / FWAAS). These
// helpers keep the view and edit modals rendering both sides consistently.

type DcPort = NonNullable<DataCenter["kontrolEdgePortA"]>;
type DcService = NonNullable<DataCenter["endpointBSdwanHub"]>;

const PortEndpointFields: React.FC<{ port: DcPort }> = ({ port }) => (
	<div className="space-y-3">
		<GenericInput label="Port ID" value={String(port.id ?? "")} readOnly />
		<GenericInput label="Sub ID" value={port.subid ?? ""} readOnly />
		<GenericInput label="Port Name" value={port.name ?? ""} readOnly />
		<GenericInput label="Tenant ID" value={String(port.tenantId ?? "")} readOnly />
		<GenericInput label="Datacenter ID" value={port.datacenterId ?? ""} readOnly />
		{port.datacenter && (
			<div className="p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary space-y-3">
				<GenericInput label="Datacenter ID" value={port.datacenter.datacenterId ?? ""} readOnly />
				<GenericInput label="Name" value={port.datacenter.name ?? ""} readOnly />
				<GenericInput label="Display Name" value={port.datacenter.displayName ?? ""} readOnly />
			</div>
		)}
	</div>
);

const ServiceEndpointFields: React.FC<{ service: DcService; kind: "sdwan_hub" | "fwaas" }> = ({ service, kind }) => (
	<div className="space-y-3">
		<GenericInput
			label={kind === "sdwan_hub" ? "SD-WAN Hub ID" : "FWAAS ID"}
			value={String(service?.id ?? "")}
			readOnly
		/>
		<GenericInput label="Name" value={service?.name ?? ""} readOnly />
		<GenericInput label="Subscription ID" value={service?.subscriptionId ?? ""} readOnly />
		<GenericInput label="Tenant ID" value={String(service?.tenantId ?? "")} readOnly />
	</div>
);

type BEndpoint =
	| { kind: "port"; port: DcPort }
	| { kind: "sdwan_hub"; service: DcService }
	| { kind: "fwaas"; service: DcService }
	| null;

const resolveBEndpoint = (dc: DataCenter): BEndpoint => {
	if (dc.endpointBType === "sdwan_hub" && dc.endpointBSdwanHub)
		return { kind: "sdwan_hub", service: dc.endpointBSdwanHub };
	if (dc.endpointBType === "fwaas" && dc.endpointBFwaas)
		return { kind: "fwaas", service: dc.endpointBFwaas };
	if (dc.kontrolEdgePortB) return { kind: "port", port: dc.kontrolEdgePortB };
	// Fallback when the type discriminator is missing but a service is present.
	if (dc.endpointBSdwanHub) return { kind: "sdwan_hub", service: dc.endpointBSdwanHub };
	if (dc.endpointBFwaas) return { kind: "fwaas", service: dc.endpointBFwaas };
	return null;
};

const B_ENDPOINT_TITLE: Record<NonNullable<BEndpoint>["kind"], string> = {
	port: "Port B",
	sdwan_hub: "Endpoint B — SD-WAN Hub",
	fwaas: "Endpoint B — FWAAS",
};

const BEndpointBlock: React.FC<{ dataCenter: DataCenter }> = ({ dataCenter }) => {
	const b = resolveBEndpoint(dataCenter);
	return (
		<div>
			<h4 className="text-md font-medium mb-3 text-semantic-text-secondary">
				{b ? B_ENDPOINT_TITLE[b.kind] : "Port B"}
			</h4>
			{b ? (
				b.kind === "port" ? (
					<PortEndpointFields port={b.port} />
				) : (
					<ServiceEndpointFields service={b.service} kind={b.kind} />
				)
			) : (
				<p className="text-sm text-semantic-text-secondary">No data available</p>
			)}
		</div>
	);
};

/** Section rendering the A (port) and B (polymorphic) endpoints side by side. */
const EndpointsSection: React.FC<{ dataCenter: DataCenter }> = ({ dataCenter }) => {
	const hasAnyEndpoint =
		dataCenter.kontrolEdgePortA ||
		dataCenter.kontrolEdgePortB ||
		dataCenter.endpointBSdwanHub ||
		dataCenter.endpointBFwaas;
	if (!hasAnyEndpoint) return null;
	return (
		<GenericCard
			isCollapsible={true}
			initialExpanded={true}
			headerContent={<h3 className="text-lg font-semibold text-semantic-text-primary">Kontrol Edge Endpoints A & B</h3>}
			variant="primary"
			shadow="flat"
			hasBorder={true}
		>
			<div className="p-4">
				<div className="grid grid-cols-2 gap-6">
					{/* Endpoint A (always a port) */}
					<div>
						<h4 className="text-md font-medium mb-3 text-semantic-text-secondary">Port A</h4>
						{dataCenter.kontrolEdgePortA ? (
							<PortEndpointFields port={dataCenter.kontrolEdgePortA} />
						) : (
							<p className="text-sm text-semantic-text-secondary">No data available</p>
						)}
					</div>

					{/* Endpoint B (port / SD-WAN hub / FWAAS) */}
					<BEndpointBlock dataCenter={dataCenter} />
				</div>
			</div>
		</GenericCard>
	);
};

// ====================== View Modal ======================
export interface DcViewModalProps {
	isOpen: boolean;
	isLoading: boolean;
	dataCenter: DataCenter | null;
	onClose: () => void;
}

export const DcViewModal: React.FC<DcViewModalProps> = ({
	isOpen,
	isLoading,
	dataCenter,
	onClose,
}) => {
	return (
		<GenericModal
			isOpen={isOpen}
			onClose={onClose}
			title="Data Center Interconnect Details"
			size="lg"
			minHeight="700px"
		>
			{isLoading ? (
				<div className="space-y-6">
					{[...Array(3)].map((_, idx) => (
						<div key={idx} className="grid grid-cols-3 gap-4">
							{[...Array(6)].map((_, i) => (
								<div key={i}>
									<SkeletonBlock h={20} w="30%" className="mb-3" />
									<SkeletonText lines={1} lineHeight={14} />
								</div>
							))}
						</div>
					))}
				</div>
			) : dataCenter ? (
				<div className="space-y-6">
					{/* Section 1: General Data */}
					<GenericCard
						isCollapsible={false}
						initialExpanded={true}
						variant="primary"
						shadow="flat"
						hasBorder={true}
					>
						<div className="p-4">
							<div className="grid grid-cols-3 gap-4">
								<GenericInput
									label="ID"
									value={String(dataCenter.id ?? "")}
									readOnly
								/>
								<GenericInput
									label="Name"
									value={dataCenter.name ?? ""}
									readOnly
								/>
                                <GenericInput
									label="Tags"
									value={Array.isArray(dataCenter.tag) ? dataCenter.tag.join(", ") : ""}
									readOnly
								/>
								<GenericInput
									label="Subscription ID"
									value={dataCenter.subscriptionId ?? ""}
									readOnly
								/>
								<GenericInput
									label="Mbps"
									value={String(dataCenter.mbps ?? "")}
									readOnly
								/>
								<GenericInput
									label="Terms"
									value={String(dataCenter.terms ?? "")}
									readOnly
								/>
								<GenericInput
									label="VLAN"
									value={dataCenter.vlan ?? ""}
									readOnly
								/>
								<GenericInput
									label="Automatic VLAN"
									value={dataCenter.automaticVlan ? "Yes" : "No"}
									readOnly
								/>
								<GenericInput
									label="Redundancy"
									value={dataCenter.redundancy ? "Yes" : "No"}
									readOnly
								/>
								<GenericInput
									label="Country Code"
									value={dataCenter.countryCode ?? ""}
									readOnly
								/>
								<GenericInput
									label="Auto Renew"
									value={dataCenter.autoRenew ? "Yes" : "No"}
									readOnly
								/>
								<GenericInput
									label="Status"
									value={dataCenter.isDraft ? "Draft" : "Active"}
									readOnly
								/>
								<GenericInput
									label="Created At"
									value={dataCenter.createdAt ? new Date(dataCenter.createdAt).toLocaleString() : ""}
									readOnly
								/>
								<GenericInput
									label="Updated At"
									value={dataCenter.updatedAt ? new Date(dataCenter.updatedAt).toLocaleString() : ""}
									readOnly
								/>
								{dataCenter.p2pAddressing && (
									<>
										<div className="col-span-3">
											<label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
												P2P Addressing
											</label>
											<div className="grid grid-cols-4 gap-4 p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
												<GenericInput
													label="Network"
													value={dataCenter.p2pAddressing.network ?? ""}
													readOnly
												/>
												<GenericInput
													label="Gateway"
													value={dataCenter.p2pAddressing.gateway ?? ""}
													readOnly
												/>
												<GenericInput
													label="Peer IP"
													value={dataCenter.p2pAddressing.peerIp ?? ""}
													readOnly
												/>
												<GenericInput
													label="Mask"
													value={dataCenter.p2pAddressing.mask ?? ""}
													readOnly
												/>
											</div>
										</div>
									</>
								)}
							</div>
						</div>
					</GenericCard>

					{/* Section 2: Kontrol Edge Port */}
					{dataCenter.kontrolEdgePort && (
						<GenericCard
							isCollapsible={true}
							initialExpanded={true}
							headerContent={<h3 className="text-lg font-semibold text-semantic-text-primary">Complementary Information</h3>}
							variant="primary"
							shadow="flat"
							hasBorder={true}
						>
							<div className="p-4">
								<div className="grid grid-cols-3 gap-4">
									<GenericInput
										label="Port ID"
										value={String(dataCenter.kontrolEdgePort.id ?? "")}
										readOnly
									/>
									<GenericInput
										label="Sub ID"
										value={dataCenter.kontrolEdgePort.subid ?? ""}
										readOnly
									/>
									<GenericInput
										label="Port Name"
										value={dataCenter.kontrolEdgePort.name ?? ""}
										readOnly
									/>
									<GenericInput
										label="Tenant ID"
										value={String(dataCenter.kontrolEdgePort.tenantId ?? "")}
										readOnly
									/>
									<GenericInput
										label="Hardware ID"
										value={dataCenter.kontrolEdgePort.hardwareId ?? ""}
										readOnly
									/>
									<GenericInput
										label="Capacity (Mbps)"
										value={String(dataCenter.kontrolEdgePort.capacityMbps ?? "")}
										readOnly
									/>
									{dataCenter.kontrolEdgePort.tenant && (
										<>
											<div className="col-span-3">
												<label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
													Tenant Information
												</label>
												<div className="grid grid-cols-4 gap-4 p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
													<GenericInput
														label="Tenant ID"
														value={String(dataCenter.kontrolEdgePort.tenant.id ?? "")}
														readOnly
													/>
													<GenericInput
														label="Name"
														value={dataCenter.kontrolEdgePort.tenant.Name ?? ""}
														readOnly
													/>
													<GenericInput
														label="Display Name"
														value={dataCenter.kontrolEdgePort.tenant.nameDisplay ?? ""}
														readOnly
													/>
													<GenericInput
														label="Chargebee Customer ID"
														value={dataCenter.kontrolEdgePort.tenant.chargebeeCustomerId ?? ""}
														readOnly
													/>
												</div>
											</div>
										</>
									)}
									{dataCenter.kontrolEdgePort.datacenter && (
										<>
											<div className="col-span-3">
												<label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
													Datacenter Information
												</label>
												<div className="grid grid-cols-4 gap-4 p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
													<GenericInput
														label="Datacenter ID"
														value={dataCenter.kontrolEdgePort.datacenter.datacenterId ?? ""}
														readOnly
													/>
													<GenericInput
														label="Name"
														value={dataCenter.kontrolEdgePort.datacenter.name ?? ""}
														readOnly
													/>
													<GenericInput
														label="Display Name"
														value={dataCenter.kontrolEdgePort.datacenter.displayName ?? ""}
														readOnly
													/>
													<GenericInput
														label="City"
														value={dataCenter.kontrolEdgePort.datacenter.city ?? ""}
														readOnly
													/>
												</div>
											</div>
										</>
									)}
									{dataCenter.kontrolEdgePort.hardware && (
										<>
											<div className="col-span-3">
												<label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
													Hardware Information
												</label>
												<div className="grid grid-cols-3 gap-4 p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
													<GenericInput
														label="Hardware ID"
														value={String(dataCenter.kontrolEdgePort.hardware.id ?? "")}
														readOnly
													/>
													<GenericInput
														label="Hostname"
														value={dataCenter.kontrolEdgePort.hardware.hostname ?? ""}
														readOnly
													/>
													<GenericInput
														label="Model"
														value={dataCenter.kontrolEdgePort.hardware.model ?? ""}
														readOnly
													/>
												</div>
											</div>
										</>
									)}
								</div>
							</div>
						</GenericCard>
					)}

					{/* Section 3: Kontrol Edge Endpoints A & B (2 columns) */}
					<EndpointsSection dataCenter={dataCenter} />
				</div>
			) : null}
			<div className="flex justify-end">
				<button
					className="
              px-4 py-2
              my-5
              text-sm
              font-medium
              text-semantic-text-secondary
              bg-semantic-bg-primary
              border border-semantic-border-secondary
              rounded
              hover:bg-semantic-state-active
              transition-colors
            "
					onClick={onClose}
				>
					Close
				</button>
			</div>
		</GenericModal>
	);
};

// ====================== Edit Modal ======================
export interface ProviderOption {
	id: number;
	name: string;
}

export interface DcEditModalProps {
	isOpen: boolean;
	editMode: boolean;
	isLoading: boolean;
	editDetailData: DataCenter | null;
	// Form state
	dcId: string;
	dcName: string;
	dcDirection: string;
	dcCapacity: string;
	dcStatus: string;
	dcGps: string;
	dcPhase: string;
	dcCage: string;
	dcRackId: string;
	dcRackCapacity: string;
	dcPatchPanelODF: string;
	dcRackSecurityPin: string;
	dcVisible: boolean;
	dcProvider: string;
	dcProviderCode: string;
	dcCity: string;
	dcDisplayName: string;
	// Provider options
	providerOptions: ProviderOption[];
	// Handlers
	onClose: () => void;
	onSave: () => void;
	onDcIdChange: (value: string) => void;
	onDcNameChange: (value: string) => void;
	onDcDirectionChange: (value: string) => void;
	onDcCapacityChange: (value: string) => void;
	onDcStatusChange: (value: string) => void;
	onDcGpsChange: (value: string) => void;
	onDcPhaseChange: (value: string) => void;
	onDcCageChange: (value: string) => void;
	onDcRackIdChange: (value: string) => void;
	onDcRackCapacityChange: (value: string) => void;
	onDcPatchPanelODFChange: (value: string) => void;
	onDcRackSecurityPinChange: (value: string) => void;
	onDcVisibleChange: (value: boolean) => void;
	onDcProviderChange: (value: string) => void;
	onDcProviderCodeChange: (value: string) => void;
	onDcCityChange: (value: string) => void;
	onDcDisplayNameChange: (value: string) => void;
	getProviderNameById: (providerId: string) => string;
	handleSelectProviderName: (selectedName: string) => void;
}

export const DcEditModal: React.FC<DcEditModalProps> = ({
	isOpen,
	editMode,
	isLoading,
	editDetailData,
	dcId,
	dcName,
	dcDirection,
	dcCapacity,
	dcStatus,
	dcGps,
	dcPhase,
	dcCage,
	dcRackId,
	dcRackCapacity,
	dcPatchPanelODF,
	dcRackSecurityPin,
	dcVisible,
	dcProvider,
	dcProviderCode,
	dcCity,
	dcDisplayName,
	providerOptions,
	onClose,
	onSave,
	onDcIdChange,
	onDcNameChange,
	onDcDirectionChange,
	onDcCapacityChange,
	onDcStatusChange,
	onDcGpsChange,
	onDcPhaseChange,
	onDcCageChange,
	onDcRackIdChange,
	onDcRackCapacityChange,
	onDcPatchPanelODFChange,
	onDcRackSecurityPinChange,
	onDcVisibleChange,
	onDcProviderChange,
	onDcProviderCodeChange,
	onDcCityChange,
	onDcDisplayNameChange,
	getProviderNameById,
	handleSelectProviderName,
}) => {
	const providerNames = providerOptions.map((opt) => opt.name);

	return (
		<GenericModal
			isOpen={isOpen}
			onClose={onClose}
			title={editMode ? "Edit Data Center" : "Add Data Center"}
			size="lg"
			minHeight="700px"
		>
			{/* 3-column grid */}
			<div className="grid grid-cols-3 gap-4 mb-4">
				<GenericInput
					label="DatacenterId"
					value={dcId}
					onChange={onDcIdChange}
					readOnly={editMode}
				/>
				<GenericInput
					label="Name"
					placeholder="e.g. DC North"
					value={dcName}
					onChange={onDcNameChange}
				/>
				<GenericInput
					label="Direction"
					placeholder="e.g. 123 North St, City"
					value={dcDirection}
					onChange={onDcDirectionChange}
				/>
				<GenericInput
					label="Gps"
					placeholder="e.g. 40.7128° N, 74.0060° W"
					value={dcGps}
					onChange={onDcGpsChange}
				/>
				<GenericInput
					label="CapacityKW"
					placeholder="e.g. 1000"
					value={dcCapacity}
					onChange={onDcCapacityChange}
				/>
				<GenericInput
					label="Phase"
					placeholder="e.g. 3-phase"
					value={dcPhase}
					onChange={onDcPhaseChange}
				/>
				<GenericInput
					label="Cage"
					placeholder="e.g. Cage A"
					value={dcCage}
					onChange={onDcCageChange}
				/>
				<GenericInput
					label="RackId"
					placeholder="e.g. RACK-001"
					value={dcRackId}
					onChange={onDcRackIdChange}
				/>
				<GenericInput
					label="RackCapacity"
					placeholder="e.g. 42"
					value={dcRackCapacity}
					onChange={onDcRackCapacityChange}
				/>
				<GenericInput
					label="PatchPanelODF"
					placeholder="e.g. ODF-001"
					value={dcPatchPanelODF}
					onChange={onDcPatchPanelODFChange}
				/>
				<GenericInput
					label="RackSecurityPin"
					placeholder="e.g. 1234"
					value={dcRackSecurityPin}
					onChange={onDcRackSecurityPinChange}
				/>
				<div className="flex flex-col space-y-2">
					<label className="text-sm font-medium">Status</label>
					<GenericDropdown
						items={["Active", "Implementation", "Deprecated"]}
						value={dcStatus}
						onSelect={onDcStatusChange}
						defaultLabel="Status"
						widthClass="w-full"
					/>
				</div>
				<div className="flex flex-col">
					<label className="text-sm font-medium">Provider</label>
					<GenericDropdown
						items={providerNames}
						value={getProviderNameById(dcProvider)}
						onSelect={handleSelectProviderName}
						defaultLabel="Select a Provider"
						widthClass="w-full"
					/>
				</div>
				<GenericInput
					label="Provider Code"
					placeholder="e.g. MEX2"
					value={dcProviderCode}
					onChange={onDcProviderCodeChange}
				/>
				<GenericInput
					label="City"
					placeholder="e.g. Santa Fe"
					value={dcCity}
					onChange={onDcCityChange}
				/>
				<GenericInput
					label="Display Name"
					placeholder="Auto or e.g. MEX2 - Santa Fe"
					value={dcDisplayName}
					disabled={true}
					onChange={onDcDisplayNameChange}
				/>
				<GenericCheckbox
					label="Make visible to end users"
					onChange={onDcVisibleChange}
					checked={dcVisible}
				/>
			</div>

			{/* Detail sections - only show when we have detail data */}
			{isLoading ? (
				<div className="mt-6 space-y-6">
					<div className="grid grid-cols-2 gap-6">
						{[...Array(6)].map((_, i) => (
							<div key={i}>
								<SkeletonBlock h={20} w="30%" className="mb-3" />
								<SkeletonText lines={1} lineHeight={14} />
							</div>
						))}
					</div>
				</div>
			) : editDetailData ? (
				<div className="mt-6 space-y-6">
					{/* Section: Kontrol Edge Endpoints A & B (2 columns) */}
					<EndpointsSection dataCenter={editDetailData} />
				</div>
			) : null}

			<div className="flex justify-end space-x-2">
				<button
					className="
              px-4 py-2
              text-sm
              font-medium
              text-semantic-text-secondary
              bg-semantic-bg-primary
              border border-semantic-border-secondary
              rounded
              hover:bg-semantic-state-active
              transition-colors
            "
					onClick={onClose}
				>
					Cancel
				</button>
				<button
					className="
              px-4 py-2
              text-sm
              font-medium
              text-white
              bg-semantic-text-brand
              rounded
              hover:bg-opacity-90
              transition-colors
            "
					onClick={onSave}
				>
					Save
				</button>
			</div>
		</GenericModal>
	);
};

